import React, { useState, useEffect, useMemo } from 'react';
import { Users, Maximize, Wifi, Wind, Coffee, Eye, ChevronLeft, ChevronRight, X, Calendar, Calculator, Loader2, AlertCircle, Flame, Clock, Tag } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { rooms as fallbackRooms } from '../data/mock';
import { useBooking } from '../context/BookingContext';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { localizeRoom, amenityLabel } from '../i18n/roomsI18n';
import { roomSlug, roomPageCopy } from '../i18n/roomPages';
import { langPath } from '../utils/siteNav';
import { optimized, srcSetFor } from '../utils/img';
import BookingModal from './BookingModal';
import RoomDetailModal from './RoomDetailModal';
import { trackRoomView, trackBookingClick } from '../hooks/useAnalytics';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const amenityIcons = {
  'Sea View': Eye,
  'Balcony': Maximize,
  'Air Conditioning': Wind,
  'Mini Bar': Coffee,
  'Free WiFi': Wifi,
  'Living Area': Maximize,
  'Bathtub': Coffee,
  'Room Service': Coffee,
  'Panoramic View': Eye,
  'Private Terrace': Maximize,
  'Jacuzzi': Coffee,
  'Butler Service': Users,
  'Premium WiFi': Wifi,
  'City View': Eye,
  'Seating Area': Maximize,
  'Minibar': Coffee,
  'Writing Desk': Maximize,
  'Soundproof Windows': Wind,
  'Flat-screen TV': Eye,
  'Private Bathroom': Coffee,
  'Premium Toiletries': Coffee,
  'Family Friendly': Users,
  'Separate Bedroom': Maximize,
  'Kitchenette': Coffee
};

const Rooms = () => {
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isRoomDetailOpen, setIsRoomDetailOpen] = useState(false);
  const [selectedRoomForDetail, setSelectedRoomForDetail] = useState(null);
  const [livePricing, setLivePricing] = useState(null);
  const [isLoadingPrices, setIsLoadingPrices] = useState(false);
  const [pricingError, setPricingError] = useState(null);
  const [rawRooms, setRooms] = useState(fallbackRooms);
  
  const { bookingDates, formatDateRange, updateBookingDates } = useBooking();
  const { t, language } = useLanguage();
  const rooms = useMemo(() => rawRooms.map((r) => localizeRoom(r, language, t)), [rawRooms, language, t]);

  // Generate consistent urgency data per room (seeded by room ID)
  const urgencyData = useMemo(() => {
    return rooms.reduce((acc, room) => {
      const seed = room.id || room.beds24Id;
      const viewingNow = ((seed * 7) % 5) + 2; // 2-6 people
      const roomsLeft = ((seed * 3) % 3) + 1; // 1-3 rooms
      const bookedToday = ((seed * 11) % 4) + 1; // 1-4 bookings
      acc[room.id] = { viewingNow, roomsLeft, bookedToday };
      return acc;
    }, {});
  }, [rooms]);

  // Fetch rooms from API on mount
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/public/rooms`);
        if (response.data && response.data.length > 0) {
          setRooms(response.data);
        }
      } catch (error) {
        console.error('Error fetching rooms from API, using fallback:', error);
      }
    };
    fetchRooms();
  }, []);

  // Fetch live pricing when dates change
  useEffect(() => {
    const fetchLivePricing = async () => {
      if (!bookingDates.checkIn || !bookingDates.checkOut || bookingDates.nights <= 0) {
        setLivePricing(null);
        return;
      }

      setIsLoadingPrices(true);
      setPricingError(null);

      try {
        const response = await axios.get(`${BACKEND_URL}/api/beds24/search-availability`, {
          params: {
            check_in: bookingDates.checkIn,
            check_out: bookingDates.checkOut,
            adults: bookingDates.guests,
            children: 0
          }
        });

        if (response.data.success) {
          setLivePricing(response.data);
        }
      } catch (error) {
        console.error('Error fetching live pricing:', error);
        setPricingError('Unable to fetch live prices. Showing estimated prices.');
      } finally {
        setIsLoadingPrices(false);
      }
    };

    fetchLivePricing();
  }, [bookingDates.checkIn, bookingDates.checkOut, bookingDates.guests, bookingDates.nights]);

  const nextImage = (roomId, e) => {
    e?.stopPropagation();
    const room = rooms.find(r => r.id === roomId);
    const images = room?.images || [room?.image];
    setCurrentImageIndex(prev => ({
      ...prev,
      [roomId]: ((prev[roomId] || 0) + 1) % images.length
    }));
  };

  const prevImage = (roomId, e) => {
    e?.stopPropagation();
    const room = rooms.find(r => r.id === roomId);
    const images = room?.images || [room?.image];
    setCurrentImageIndex(prev => ({
      ...prev,
      [roomId]: ((prev[roomId] || 0) - 1 + images.length) % images.length
    }));
  };

  const getCurrentImage = (room) => {
    const images = room.images || [room.image];
    const index = currentImageIndex[room.id] || 0;
    return images[index];
  };

  const getImageCount = (room) => {
    return room.images?.length || 1;
  };

  // Get live price for a room or fall back to static price
  const getRoomPrice = (room) => {
    if (livePricing?.rooms?.length > 0) {
      // Match by beds24Id (room_id) for accurate price matching
      const liveRoom = livePricing.rooms.find(r => 
        r.room_id === room.beds24Id || r.room_id === room.id
      );
      if (liveRoom) {
        // Beds24 returned this room. Trust their availability + price signal.
        if (liveRoom.total_price > 0 && liveRoom.available) {
          return {
            total: liveRoom.total_price,
            perNight: liveRoom.price_per_night,
            isLive: true,
            available: true,
            currency: liveRoom.currency || 'EUR',
          };
        }
        // Beds24 explicitly says this room is not sellable for these dates.
        // Do NOT show a fake "starting from €X" — mark it unavailable.
        return {
          total: null,
          perNight: null,
          isLive: true,
          available: false,
          currency: 'EUR',
        };
      }
    }

    // No live pricing data at all (Beds24 didn't return this room, or search
    // hasn't run yet). Show the static base rate as a starting-from hint.
    if (bookingDates.nights > 0) {
      return {
        total: null, // Don't show total to avoid confusion
        perNight: room.price,
        isLive: false,
        available: true,
        currency: 'EUR',
      };
    }

    return null;
  };

  const handleBookRoom = (room = null) => {
    if (room && bookingDates.checkIn && bookingDates.checkOut) {
      // Track room view
      trackRoomView(room.beds24Id);
      // If dates are selected, open room detail modal
      const priceData = getRoomPrice(room);
      setSelectedRoomForDetail(room);
      setIsRoomDetailOpen(true);
    } else {
      // Track booking click
      trackBookingClick();
      // If no dates, open regular booking modal
      setIsBookingModalOpen(true);
    }
  };

  const handleRoomDetailBook = (room, bookingData) => {
    // Close room detail modal and open Beds24 booking
    setIsRoomDetailOpen(false);
    setIsBookingModalOpen(true);
  };

  return (
    <section id="rooms" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-amber-700 font-medium tracking-widest text-sm uppercase">{t('rooms.subtitle')}</span>
          <h2 className="text-4xl md:text-5xl font-serif text-slate-800 mt-3 mb-4">{t('rooms.title')}</h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg">
            {t('rooms.description')}
          </p>
        </div>

        {/* Selected Dates Banner with Live Pricing Status */}
        {bookingDates.nights > 0 && (
          <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-amber-800 font-semibold">{t('rooms.yourStay')}: {formatDateRange()}</p>
                  <p className="text-amber-600 text-sm">{bookingDates.nights} {bookingDates.nights > 1 ? t('hero.nights') : t('hero.night')} · {bookingDates.guests} {bookingDates.guests > 1 ? t('hero.guests') : t('hero.guest')}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {isLoadingPrices ? (
                  <div className="flex items-center gap-2 text-amber-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">{t('hero.searching')}</span>
                  </div>
                ) : livePricing?.rooms?.length > 0 ? (
                  <div className="flex items-center gap-2 text-green-700">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    <span className="text-sm font-medium">{t('rooms.livePrice')}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-blue-600">
                    <span className="text-sm">{t('rooms.seeRealTimePrices')}</span>
                  </div>
                )}
                <Button 
                  onClick={() => handleBookRoom()}
                  className="bg-amber-700 hover:bg-amber-800 text-white"
                >
                  {t('rooms.checkPriceBook')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Rooms Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rooms.map((room, index) => {
            const pricing = getRoomPrice(room);
            
            return (
              <div
                key={room.id}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Room Image Carousel */}
                <div className="relative h-64 overflow-hidden bg-slate-200">
                  <img
                    src={optimized(getCurrentImage(room), 800)}
                    srcSet={srcSetFor(getCurrentImage(room))}
                    sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
                    alt={room.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                  
                  {/* Image Navigation */}
                  {getImageCount(room) > 1 && (
                    <>
                      <button
                        onClick={(e) => prevImage(room.id, e)}
                        aria-label="Previous photo" className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <ChevronLeft className="w-5 h-5 text-slate-700" />
                      </button>
                      <button
                        onClick={(e) => nextImage(room.id, e)}
                        aria-label="Next photo" className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <ChevronRight className="w-5 h-5 text-slate-700" />
                      </button>
                      
                      {/* Image Dots */}
                      <div className="absolute top-3 right-3 flex bg-black/25 rounded-full">
                        {(room.images || [room.image]).map((img, imgIndex) => (
                          <button
                            key={`${room.id}-${img}`}
                            type="button"
                            aria-label={`${room.name} — photo ${imgIndex + 1}`}
                            aria-current={(currentImageIndex[room.id] || 0) === imgIndex}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentImageIndex(prev => ({ ...prev, [room.id]: imgIndex }));
                            }}
                            className="p-2 flex items-center"
                          >
                            <span className={`block h-2 rounded-full transition-all ${
                              (currentImageIndex[room.id] || 0) === imgIndex
                                ? 'bg-white w-4'
                                : 'w-2 bg-white/50 hover:bg-white/80'
                            }`} />
                          </button>
                        ))}
                      </div>

                      {/* Image Counter */}
                      <div className="absolute top-4 left-4 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                        {(currentImageIndex[room.id] || 0) + 1} / {getImageCount(room)}
                      </div>
                    </>
                  )}
                  
                  {/* Price Badge — hidden when Beds24 says this room isn't sellable */}
                  {(!pricing || pricing.available !== false) && (
                    <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                      {pricing?.isLive && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></span>
                      )}
                      <span className="text-amber-700 font-bold text-lg">
                        {pricing?.perNight ? `€${Math.round(pricing.perNight)}` : `€${room.price}`}
                      </span>
                      <span className="text-slate-500 text-sm">{t('rooms.perNight')}</span>
                    </div>
                  )}

                  {/* Room Name Overlay */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white text-xl font-semibold"><Link to={langPath(language, `rooms/${roomSlug(room)}`)} className="hover:text-amber-300 transition-colors" data-testid={`room-title-link-${roomSlug(room)}`}>{room.name}</Link></h3>
                    <div className="flex items-center gap-4 mt-2 text-white/80 text-sm">
                      <span className="flex items-center gap-1">
                        <Maximize className="w-4 h-4" />
                        {room.size}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {room.guests} {room.guests > 1 ? t('hero.guests') : t('hero.guest')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Room Details */}
                <div className="p-6">
                  <p className="text-slate-600 mb-1 line-clamp-2">{room.description}</p>
                  <Link to={langPath(language, `rooms/${roomSlug(room)}`)} className="inline-flex items-center gap-1 text-sm text-amber-700 hover:text-amber-800 font-medium mb-4" data-testid={`room-page-link-${roomSlug(room)}`}>{roomPageCopy(language, room).labels.about} →</Link>
                  
                  {/* Price Info */}
                  {pricing && bookingDates.nights > 0 && pricing.available && (
                    <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-amber-100/50 rounded-xl border border-amber-200">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-xs text-amber-600 font-medium uppercase tracking-wide">
                            {pricing.isLive ? t('rooms.livePrice') : t('rooms.priceLabel')}
                          </p>
                          <p className="text-2xl font-bold text-amber-700">
                            {pricing.isLive ? `€${Math.round(pricing.total)}` : `€${pricing.perNight}${t('rooms.perNight')}`}
                          </p>
                        </div>
                        {pricing.available && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                            ✓ {t('rooms.available')}
                          </span>
                        )}
                      </div>
                      
                      {/* Price Info */}
                      <div className="space-y-1 text-sm border-t border-amber-200 pt-3">
                        {pricing.isLive ? (
                          <>
                            <div className="flex justify-between text-slate-600">
                              <span>€{Math.round(pricing.perNight)} × {bookingDates.nights} {bookingDates.nights > 1 ? t('hero.nights') : t('hero.night')}</span>
                              <span>€{Math.round(pricing.total)}</span>
                            </div>
                            <div className="flex justify-between text-slate-500 text-xs">
                              <span>{t('rooms.taxesIncluded')}</span>
                            </div>
                          </>
                        ) : (
                          <div className="text-slate-600 text-sm">
                            <span className="font-medium">{t('rooms.forNights')} {bookingDates.nights} {bookingDates.nights > 1 ? t('hero.nights') : t('hero.night')}</span>
                            <p className="text-xs text-slate-500 mt-1">
                              {t('rooms.clickToSeePrice')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Unavailable state: Beds24 explicitly says this room isn't sellable for the chosen dates */}
                  {pricing && bookingDates.nights > 0 && !pricing.available && (
                    <div
                      className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-xl"
                      data-testid={`room-unavailable-${room.id}`}
                    >
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">
                        Not available for selected dates
                      </p>
                      <p className="text-sm text-slate-600 mt-1">
                        Please try different dates or contact us directly.
                      </p>
                    </div>
                  )}
                  
                  {/* Amenities */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {(room.amenities || []).slice(0, 4).map((amenity) => {
                      const Icon = amenityIcons[amenity] || Wifi;
                      return (
                        <Badge
                          key={amenity}
                          variant="secondary"
                          className="bg-amber-50 text-amber-700 border-amber-200 flex items-center gap-1 px-3 py-1"
                        >
                          <Icon className="w-3 h-3" />
                          {amenityLabel(amenity, t)}
                        </Badge>
                      );
                    })}
                  </div>

                  {/* Urgency Indicators */}
                  <div className="mb-4 space-y-1.5" data-testid={`urgency-strip-${room.id}`}>
                    <div className="flex items-center gap-1.5 text-xs text-red-600">
                      <Flame className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="font-medium">
                        {t('rooms.onlyRoomsLeft') ? t('rooms.onlyRoomsLeft').replace('{n}', urgencyData[room.id]?.roomsLeft) : `Only ${urgencyData[room.id]?.roomsLeft} left at this price`}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Eye className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>
                        {t('rooms.viewingNow') ? t('rooms.viewingNow').replace('{n}', urgencyData[room.id]?.viewingNow) : `${urgencyData[room.id]?.viewingNow} people viewing right now`}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-green-700">
                      <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>
                        {t('rooms.bookedToday') ? t('rooms.bookedToday').replace('{n}', urgencyData[room.id]?.bookedToday) : `Booked ${urgencyData[room.id]?.bookedToday}x today`}
                      </span>
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="space-y-2">
                    <Button
                      onClick={() => setSelectedRoom(room)}
                      variant="outline"
                      className="w-full border-slate-300 hover:border-amber-500 text-slate-700 hover:text-amber-700 rounded-lg py-3"
                    >
                      {t('rooms.viewDetails')}
                    </Button>
                    <Button
                      onClick={() => handleBookRoom(room)}
                      disabled={pricing && !pricing.available}
                      className="w-full bg-amber-700 hover:bg-amber-800 text-white rounded-lg py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {pricing && !pricing.available
                        ? 'Not available'
                        : pricing?.isLive
                          ? `Book for €${Math.round(pricing.total)}`
                          : t('rooms.checkPriceBook')}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Room Detail Modal */}
        {selectedRoom && (
          <div
            className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedRoom(null)}
          >
            <div
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Image Gallery */}
              <div className="relative h-80 md:h-96">
                <img
                  src={optimized(getCurrentImage(selectedRoom), 1200)}
                  srcSet={srcSetFor(getCurrentImage(selectedRoom), [800, 1200, 1600])}
                  sizes="(min-width: 1024px) 900px, 100vw"
                  alt={selectedRoom.name}
                  className="w-full h-full object-cover"
                />
                
                <button
                  onClick={() => setSelectedRoom(null)}
                  className="absolute top-4 right-4 bg-white/90 rounded-full p-2 shadow-lg hover:bg-white transition-colors"
                >
                  <X className="w-5 h-5 text-slate-700" />
                </button>

                {getImageCount(selectedRoom) > 1 && (
                  <>
                    <button
                      onClick={(e) => prevImage(selectedRoom.id, e)}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg"
                    >
                      <ChevronLeft className="w-6 h-6 text-slate-700" />
                    </button>
                    <button
                      onClick={(e) => nextImage(selectedRoom.id, e)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg"
                    >
                      <ChevronRight className="w-6 h-6 text-slate-700" />
                    </button>
                  </>
                )}

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm">
                  {(currentImageIndex[selectedRoom.id] || 0) + 1} / {getImageCount(selectedRoom)} {t('rooms.photos')}
                </div>
              </div>

              {/* Thumbnails */}
              {selectedRoom.images && selectedRoom.images.length > 1 && (
                <div className="flex gap-2 p-4 bg-slate-100 overflow-x-auto">
                  {selectedRoom.images.map((img, idx) => (
                    <button
                      key={`${selectedRoom.id}-thumb-${img}`}
                      onClick={() => setCurrentImageIndex(prev => ({ ...prev, [selectedRoom.id]: idx }))}
                      className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        (currentImageIndex[selectedRoom.id] || 0) === idx
                          ? 'border-amber-500 shadow-lg'
                          : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={optimized(img, 400)} alt={`${selectedRoom.name} ${idx + 1}`} loading="lazy" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {/* Room Details */}
              <div className="p-8">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-2xl font-serif text-slate-800">{selectedRoom.name}</h3>
                    <div className="flex items-center gap-4 mt-2 text-slate-500">
                      <span className="flex items-center gap-1">
                        <Maximize className="w-4 h-4" />
                        {selectedRoom.size}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {selectedRoom.guests} {selectedRoom.guests > 1 ? t('hero.guests') : t('hero.guest')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Price Info in Modal */}
                {(() => {
                  const pricing = getRoomPrice(selectedRoom);
                  if (pricing && bookingDates.nights > 0) {
                    return (
                      <div className="mb-6 p-5 bg-gradient-to-r from-amber-50 to-amber-100 rounded-xl border border-amber-200">
                        <div className="flex items-center gap-2 mb-3">
                          <Calculator className="w-5 h-5 text-amber-600" />
                          <h4 className="font-semibold text-amber-800">
                            {pricing.isLive ? t('rooms.livePrice') : t('rooms.roomInfo')}
                          </h4>
                          {pricing.isLive && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Live</span>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex justify-between text-slate-700">
                            <span>{pricing.isLive ? t('rooms.roomRate') : t('rooms.priceLabel')}</span>
                            <span>€{Math.round(pricing.perNight)}{t('rooms.perNight')}</span>
                          </div>
                          <div className="flex justify-between text-slate-700">
                            <span>{t('rooms.numberOfNights')}</span>
                            <span>{bookingDates.nights} {bookingDates.nights > 1 ? t('hero.nights') : t('hero.night')}</span>
                          </div>
                          <div className="flex justify-between text-slate-700">
                            <span>{t('rooms.dates')}</span>
                            <span>{formatDateRange()}</span>
                          </div>
                          <div className="flex justify-between text-slate-700">
                            <span>{t('hero.guests')}</span>
                            <span>{bookingDates.guests}</span>
                          </div>
                          {pricing.isLive ? (
                            <div className="border-t border-amber-300 pt-2 mt-2">
                              <div className="flex justify-between text-lg font-bold text-amber-800">
                                <span>{t('rooms.total')}</span>
                                <span>€{Math.round(pricing.total)}</span>
                              </div>
                              <p className="text-xs text-amber-600 mt-1">{t('rooms.taxesIncluded')}</p>
                            </div>
                          ) : (
                            <div className="border-t border-amber-300 pt-2 mt-2">
                              <p className="text-sm text-amber-700 font-medium">
                                {t('rooms.clickToSeePrice')}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="mb-6 p-4 bg-slate-100 rounded-xl text-center">
                      <p className="text-slate-600">{t('booking.selectDates')}</p>
                    </div>
                  );
                })()}

                <p className="text-slate-600 mb-6">{selectedRoom.description}</p>
                
                <h4 className="font-semibold text-slate-800 mb-3">{t('nav.amenities')}</h4>
                <div className="flex flex-wrap gap-2 mb-6">
                  {(selectedRoom.amenities || []).map((amenity) => {
                    const Icon = amenityIcons[amenity] || Wifi;
                    return (
                      <Badge
                        key={amenity}
                        className="bg-amber-50 text-amber-700 border-amber-200 flex items-center gap-1.5 px-4 py-2"
                      >
                        <Icon className="w-4 h-4" />
                        {amenityLabel(amenity, t)}
                      </Badge>
                    );
                  })}
                </div>
                
                <Button
                  onClick={() => {
                    setSelectedRoom(null);
                    handleBookRoom(selectedRoom);
                  }}
                  className="w-full bg-amber-700 hover:bg-amber-800 text-white py-4 rounded-lg text-lg"
                >
                  {(() => {
                    const pricing = getRoomPrice(selectedRoom);
                    return pricing?.isLive && bookingDates.nights > 0 
                      ? `€${Math.round(pricing.total)} - ${t('rooms.checkPriceBookNow')}`
                      : t('rooms.checkPriceBookNow');
                  })()}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Booking Modal with Embedded Beds24 */}
        <BookingModal 
          isOpen={isBookingModalOpen}
          onClose={() => setIsBookingModalOpen(false)}
          initialDates={{
            checkIn: bookingDates.checkIn,
            checkOut: bookingDates.checkOut,
            guests: bookingDates.guests
          }}
        />

        {/* Room Detail Modal */}
        <RoomDetailModal
          isOpen={isRoomDetailOpen}
          onClose={() => setIsRoomDetailOpen(false)}
          room={selectedRoomForDetail}
          checkIn={bookingDates.checkIn}
          checkOut={bookingDates.checkOut}
          guests={bookingDates.guests}
          priceData={selectedRoomForDetail ? getRoomPrice(selectedRoomForDetail) : null}
          onBook={handleRoomDetailBook}
        />
      </div>
    </section>
  );
};

export default Rooms;
